import { Avatar, Button, Col, Dropdown, Image, Menu, Row, Tag, Tooltip, Typography } from 'antd';
import cronstrue from 'cronstrue';
import React, { ReactElement } from 'react';
import styled from 'styled-components/macro';
import { InfoCircleOutlined } from '@ant-design/icons';
import { capitalizeFirstLetter } from '../../shared/textUtil';
import useGetSourceLogoUrl from './builder/useGetSourceLogoUrl';
import {
    getExecutionRequestStatusDisplayColor,
    getExecutionRequestStatusDisplayText,
    getExecutionRequestStatusIcon,
    RUNNING,
    SCHEDULED,
    CREATED,
} from './utils';
import { EntityType } from '../../../types.generated';
import { IconStyleType } from '../../entity/Entity';
import { useEntityRegistry } from '../../useEntityRegistry';
import { ReactComponent as IconMoreVert } from '../../../images/icon-more-vert.svg';
import i18n from '../../../i18n/i18n';

const StatusContainer = styled.div`
    display: flex;
    justify-content: left;
    align-items: center;
`;

const StatusButton = styled(Button)`
    padding: 0px;
    margin: 0px;
`;

const ActionButtonContainer = styled.div`
    text-align: right;
`;

interface TypeColumnProps {
    type: string;
}
const STATUS = [SCHEDULED, RUNNING];

export const PREFIX_REMOVE_INGESTION = {
    DATA_HUB: 'datahub-',
};

export function TypeColumn({ type }: TypeColumnProps) {
    const iconUrl = useGetSourceLogoUrl(type);
    const entityRegistry = useEntityRegistry();

    // Here, we check if any type has a glossary term and the iconUrl is empty, then we show the entity glossary item.
    let EntityIcon: ReactElement | null = null;
    if (!iconUrl) {
        EntityIcon = type.includes('glossary')
            ? entityRegistry.getIcon(EntityType.GlossaryTerm, 35, IconStyleType.SVG)
            : null;
    }

    // Here, we check if the type contains '-', then we will remove the dashes and the data-hub word from the type.
    let modifiedType = '';
    if (type.includes('-')) {
        modifiedType = type.replace(PREFIX_REMOVE_INGESTION.DATA_HUB, '').replaceAll('-', '');
    }
    const typeDisplayName = capitalizeFirstLetter(modifiedType || type);

    return (
        <Row wrap={false} align="middle" className="f-gap-2">
            <Col flex="none">
                {EntityIcon || (
                    <Avatar
                        className="f-avatar-small f-avatar-black"
                        icon={<Image preview={false} src={iconUrl} alt={type || ''} />}
                    />
                )}
            </Col>
            <Col flex="auto">
                <div className="f-text-medium-content f-color-dark-black-s100">{typeDisplayName}</div>
            </Col>
        </Row>
    );
}

export function LastExecutionColumn(time: any) {
    const { t } = i18n;
    const executionDate = time && new Date(time);
    const localTime =
        executionDate && `${executionDate.toLocaleDateString()} ${t('at')} ${executionDate.toLocaleTimeString()}`;
    return <Typography.Text className="f-white-space-nowrap">{localTime || t('None')}</Typography.Text>;
}

export function ScheduleColumn(schedule: any, record: any) {
    const { t } = i18n;
    const tooltip = schedule
        ? t('Runs {{schedule}} ({{timezone}})', {
              schedule: cronstrue.toString(schedule).toLowerCase(),
              timezone: record.timezone,
          })
        : t('Not scheduled');
    return (
        <Tooltip title={tooltip || t('Not scheduled')}>
            <Typography.Text code>{schedule || t('None')}</Typography.Text>
        </Tooltip>
    );
}

interface LastStatusProps {
    status: any;
    record: any;
    setFocusExecutionUrn: (urn: string) => void;
}

export function CurrentStatusColumn({ status, failedReason }: any) {
    const { t } = i18n;
    const text = t(getExecutionRequestStatusDisplayText(status));
    const color = getExecutionRequestStatusDisplayColor(status);

    let statusClass = 'f-tag-gray-outline';
    if (color === 'green') {
        statusClass = 'ant-tag-success';
    } else if (color === 'red') {
        statusClass = 'des-tag';
    }

    return (
        <Tooltip title={failedReason || ''}>
            {text ? <Tag className={`label-tag f-outlined ma-0 ${statusClass}`}>{text}</Tag> : '-'}
        </Tooltip>
    );
}

export function LastStatusColumn({ status, record, setFocusExecutionUrn }: LastStatusProps) {
    const { t } = i18n;
    const Icon = getExecutionRequestStatusIcon(status);
    const text = getExecutionRequestStatusDisplayText(status);
    const color = getExecutionRequestStatusDisplayColor(status);
    return (
        <StatusContainer>
            {Icon && <Icon style={{ color, fontSize: 14 }} />}
            <StatusButton
                data-testid="ingestion-source-table-status"
                type="link"
                onClick={() => setFocusExecutionUrn(record.lastExecUrn)}
            >
                <Typography.Text strong style={{ color, marginLeft: 8 }}>
                    {t(text) || t('Pending')}
                </Typography.Text>
            </StatusButton>
        </StatusContainer>
    );
}

interface ActionsColumnProps {
    record: any;
    setFocusExecutionUrn: (urn: string) => void;
    onExecute: (urn: string) => void;
    onEdit: (urn: string) => void;
    onView: (urn: string) => void;
    onDelete: (urn: string) => void;
}

export function ActionsColumn({
    record,
    onEdit,
    setFocusExecutionUrn,
    onView,
    onExecute,
    onDelete,
}: ActionsColumnProps) {
    const { t } = i18n;
    // Check if this is a CLI business-glossary ingestion - hide View and Run buttons for this type
    const isCliBusinessGlossary = record.cliIngestion && record.type?.includes('business-glossary');

    return (
        <ActionButtonContainer>
            <Dropdown
                trigger={['click']}
                placement="bottomRight"
                overlay={
                    <Menu className="f-dropdown-small">
                        {navigator.clipboard && (
                            <Menu.Item onClick={() => navigator.clipboard.writeText(record.urn)}>
                                {t('Copy URN')}
                            </Menu.Item>
                        )}
                        {!record.cliIngestion && (
                            <Tooltip
                                title={
                                    record.currentStatus !== CREATED
                                        ? t('You can only edit an ingestion source if there are no jobs executed')
                                        : ''
                                }
                                placement="topLeft"
                            >
                                <span>
                                    <Menu.Item
                                        disabled={record.currentStatus !== CREATED}
                                        onClick={() => onEdit(record.urn)}
                                        data-testid="ingestion-source-table-edit-button"
                                        itemIcon={record.currentStatus !== CREATED ? <InfoCircleOutlined /> : null}
                                    >
                                        {t('Edit')}
                                    </Menu.Item>
                                </span>
                            </Tooltip>
                        )}
                        {record.cliIngestion && !isCliBusinessGlossary && (
                            <Menu.Item onClick={() => onView(record.urn)}>{t('View')}</Menu.Item>
                        )}
                        {record.lastExecStatus !== RUNNING && !isCliBusinessGlossary && (
                            <Menu.Item
                                disabled={!record.cliIngestionByAppliance || STATUS.includes(record.currentStatus)}
                                onClick={() => onExecute(record.urn)}
                            >
                                {t('Run')}
                            </Menu.Item>
                        )}
                        {record.lastExecStatus === RUNNING && (
                            <Menu.Item onClick={() => setFocusExecutionUrn(record.lastExecUrn)}>
                                {t('Details')}
                            </Menu.Item>
                        )}
                        <Menu.Item onClick={() => onDelete(record.urn)} data-testid="delete-button">
                            {t('Delete')}
                        </Menu.Item>
                    </Menu>
                }
            >
                <Button
                    type="text"
                    title={t('More Actions')}
                    icon={<IconMoreVert />}
                    aria-label={t('More Actions')}
                    className="icon-button ma-0"
                />
            </Dropdown>
        </ActionButtonContainer>
    );
}
